import { Home, Menu } from '@mui/icons-material';
import { Autocomplete, CardContent, Typography, TextField } from '@mui/material';
import { ascend, insert, pipe, reject, sort } from 'ramda';
import { useMemo } from 'react';

import { Icon } from 'shared/components/ui';
import { SchoolConfigurationLayout } from 'shared/graphql';

import { MainNavigationCard } from '../MainNavigationCard/MainNavigationCard';
import { PreviewsContainer, PreviewsItem } from './MainNavigationMobileNav.styles';

interface Props {
  /**
   * The list of items in the mobile navbar.
   */
  items: SchoolConfigurationLayout[];

  /**
   * The list of available options to choose from.
   */
  availableOptions: SchoolConfigurationLayout[];

  /**
   * Updates the list of items in the mobile navbar.
   * @param items - The list of items to update
   */
  onListUpdate(items: SchoolConfigurationLayout[]): void;
}

const CUSTOMIZABLE_ITEMS_LENGTH = 3;

export function MainNavigationMobileNav(props: Props) {
  const menu = useMemo(() => {
    const pages = [...props.items];

    return pages
      .sort(ascend(page => page?.navbarOrderNumber as number))
      .slice(0, CUSTOMIZABLE_ITEMS_LENGTH);
  }, [props.items]);

  const onChange = (position: number) => {
    return (_event: unknown, selected: SchoolConfigurationLayout | null) => {
      if (!selected) {
        return;
      }

      const sortLayout = pipe(
        // Remove the selected item from the list
        reject<SchoolConfigurationLayout>(i => i.id === selected.id),
        // Sort the items by position
        sort<SchoolConfigurationLayout>(ascend(i => i.navbarOrderNumber!)),
        // Insert the selected item at the desired position
        insert(position, selected),
        // Reassign the position to maintain consistent ordering
        items => items.map((i, idx) => ({ ...i, navbarOrderNumber: idx })),
      );

      const sorted = sortLayout(props.items);
      props.onListUpdate(sorted);
    };
  };

  return (
    <MainNavigationCard
      title="Mobile Navbar"
      subtitle="Customize the 3 central items on the mobile nav"
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {menu.map((item, position) => {
          const excludeSelected = props.availableOptions.filter(i => i.id !== item.id);

          return (
            <Autocomplete
              key={item?.id ?? position}
              value={item}
              options={excludeSelected}
              getOptionLabel={option => option.name as string}
              isOptionEqualToValue={(option, item) => option.id === item?.id}
              onChange={onChange(position)}
              renderInput={params => (
                <TextField {...params} variant="outlined" label={`Item #${position + 1}`} />
              )}
            />
          );
        })}

        <Typography variant="body2" color="textSecondary">
          Preview
        </Typography>

        <PreviewsContainer>
          <PreviewsItem>
            <Home />
          </PreviewsItem>

          {menu
            .filter(item => item.icon)
            .map((item, index) => (
              <PreviewsItem key={item.id ?? index}>
                <Icon name={item.icon!} />
              </PreviewsItem>
            ))}

          <PreviewsItem>
            <Menu />
          </PreviewsItem>
        </PreviewsContainer>
      </CardContent>
    </MainNavigationCard>
  );
}
